import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TelegramMiniMultiInputForm from "../../kit/MiniMultiInputForm/TelegramMiniMultiInputForm";
import { useTelegram } from "../../../hooks/useTelegram";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "../../providers/AlertProvider";
import {
  PATH_CHECKOUT,
  PATH_ORDER_AMENDMENT,
  PATH_ORDER_MANAGEMENT,
  PATH_PAYMENT,
} from "../../../constants/Paths";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlags";
import OrderAmendmentConfirmationDialog from "../../kit/OrderAmendmentConfirmationDialog/OrderAmendmentConfirmationDialog";
import TelegramScreen from "../../kit/Screen/TelegramScreen";
import {
  clearRequiredDeliveryCharges,
  clearRequiredExclusivePromotionCharges,
  clearRequiredSurchargeCharges,
  replaceCart,
  updateAgentPolicyManualPricing,
  updateBaseManualPricing,
  updateRequiredDeliveryCharges,
  updateRequiredExclusivePromotionCharges,
  updateRequiredSurchargeCharges,
  updateRouteDetails,
  updateSelectedTimeSlot,
  updateUserProfileDetails,
} from "../../kit/Redux/Actions";
import {
  prepareOrder,
  validateAvailableInventory,
  proceedAddToExistingOrderToServer,
} from "../../../logic/server/OrderFunction";
import { mergeCartsForAmendment } from "../../utils/review-order";
import { buildOrderPayloadV2 } from "../../../logic/server/PayloadBuilder";

/**
 * /messages endpoint is used to send messages to the bot server using a REST API.
 * The bot will receive the message and echo it in the chat.
 *
 * @see ConfirmUserDetailsFormMessage
 */
const ConfirmUserDetailsFormMessage = () => {
  const { webApp, user } = useTelegram();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerAlert } = useAlert();

  // State for amendment confirmation dialog (development only)
  const [showAmendmentDialog, setShowAmendmentDialog] = useState(false);
  const [pendingAmendmentData, setPendingAmendmentData] = useState(null);

  const agent = useSelector((state) => state.agent);
  const cartItems = useSelector((state) => state.cartItems.cartItems);
  const sessionToken = useSelector((state) => state.sessionToken);
  const surchargeChargeList = useSelector((state) => state.surchargeChargeList);
  const deliveryChargeList = useSelector((state) => state.deliveryChargeList);
  const exclusivePromotionChargeList = useSelector(
    (state) => state.exclusivePromotionChargeList
  );
  const selectedStore = useSelector((state) => state.selectedStore);
  // This id will be used by our backend to send messages to the chat with the user
  const queryId = webApp.initDataUnsafe?.query_id;

  const onMainClick = async ({ name, phone, address, timeSlot }) => {
    try {
      // Enabling/disabling the button
      webApp.BackButton.hide();
      webApp.MainButton.disable();
      webApp.MainButton.showProgress(false);

      if (queryId) {
        const { isAvailable, updatedCartItems } =
          await validateAvailableInventory(
            sessionToken,
            cartItems,
            null,
            selectedStore
          );

        if (!isAvailable) {
          alert(
            "Some items in your cart are unavailable.\n\nPlease check and confirm to proceed anyways."
          );

          dispatch(replaceCart(updatedCartItems));

          navigate(PATH_CHECKOUT, {
            replace: true,
          });

          webApp.MainButton.hideProgress(true);
        } else {
          proceedOrder(name, phone, address, timeSlot, cartItems);
        }
      }
    } catch (error) {
      webApp.MainButton.hideProgress(true);
      webApp.MainButton.enable();
      webApp.BackButton.show();

      console.error("Error sending message:", error);
      // Handle the error gracefully, e.g., show an error message to the user
      alert(`${error}`);
    }
  };

  const proceedOrder = async (name, phone, address, timeSlot, cart) => {
    try {
      // Extract the first two digits from the address variable
      const routeCode = address.substr(0, 2);

      if (!timeSlot) {
        throw new Error("Invalid timeslot.");
      }

      const userDetails = {
        userId: user?.id,
        agentId: agent.id,
      };

      const userProfile = {
        contactname: name,
        contactphone: phone,
        postalcode: address,
      };

      const orderProfile = {
        userProfile: userProfile,
        userDetails: userDetails,
        timeSlot: timeSlot,
      };

      // Prior to getting the surcharges, let's just clear it first
      dispatch(clearRequiredDeliveryCharges());
      dispatch(clearRequiredSurchargeCharges());
      // dispatch(clearRequiredConcurrentPromotionCharges());
      dispatch(clearRequiredExclusivePromotionCharges());

      const charges = [];

      if (surchargeChargeList && surchargeChargeList.length > 0) {
        // Let's check for Postal Code Surcharges
        const postalCodeChargeList = surchargeChargeList.filter(
          (item) => item.qualifier.toLowerCase() === "customeraddress"
        );

        if (postalCodeChargeList && postalCodeChargeList.length > 0) {
          const selectedPostalCodeCharge = postalCodeChargeList.find(
            (item) => item.qualifyingvalue === routeCode
          );

          if (selectedPostalCodeCharge) {
            const chargeId = selectedPostalCodeCharge.id;
            const chargeDetailCode = address;
            const requestCode = "getChargeByCode";
            const requestTitle = process.env.REACT_APP_SURCHARGE_CHARGES;
            const payload = {
              RequestTitle: requestTitle,
              RequestCode: requestCode,
              ChargeId: chargeId,
              ChargeDetailCode: chargeDetailCode,
            };

            if (charges) {
              charges.push(payload);
            }
          }
        }
      }

      if (deliveryChargeList && deliveryChargeList.length > 0) {
        // Let's check for Delivery Charges
        // Let's get the 'Standard' Delivery for now..
        const deliveryType = "Standard";

        const selectedDeliveryCharge = deliveryChargeList.find(
          (item) => item.qualifier.toLowerCase() === "delivery"
        );

        if (selectedDeliveryCharge) {
          const chargeId = selectedDeliveryCharge.id;
          const chargeDetailCode = deliveryType;
          const requestCode = "getChargeByCode";
          const requestTitle = process.env.REACT_APP_DELIVERY_CHARGES;

          const payload = {
            RequestTitle: requestTitle,
            RequestCode: requestCode,
            ChargeId: chargeId,
            ChargeDetailCode: chargeDetailCode,
          };

          if (charges) {
            charges.push(payload);
          }
        }
      }

      if (
        exclusivePromotionChargeList &&
        exclusivePromotionChargeList.length > 0
      ) {
        // Let's check the first type of  promotion
        // More than No Of Items
        const totalQtyInCart = cart.reduce(
          (total, item) => total + item.quantity,
          0
        );

        const selectedCharge = exclusivePromotionChargeList.find(
          (item) => item.qualifier.toLowerCase() === "noofitems"
        );

        if (selectedCharge) {
          const chargeId = selectedCharge.id;
          const operator = selectedCharge.qualifyingvalue;
          const chargeDetailCode = totalQtyInCart;
          const requestCode = "getChargesByCodeNOperator";
          const requestTitle = process.env.REACT_APP_EXCLUSIVE_PROMOTION;

          const payload = {
            RequestTitle: requestTitle,
            RequestCode: requestCode,
            ChargeId: chargeId,
            ChargeDetailCode: chargeDetailCode,
            ChargeDetailOperator: operator,
          };

          if (charges) {
            charges.push(payload);
          }
        }
      }

      const orderPreparation = await prepareOrder(
        sessionToken,
        cart,
        charges,
        routeCode,
        orderProfile,
        selectedStore
      );

      const { chargeList, referenceRoute, settings } = orderPreparation;

      if (!referenceRoute) {
        throw new Error("No routes were found based on your postal code.");
      }

      const routeId = referenceRoute.routeid;

      if (!routeId) {
        throw new Error("No routes were found based on your postal code.");
      }

      dispatch(updateRouteDetails(referenceRoute));

      if (chargeList && chargeList.length > 0) {
        const exclusivePromotions = chargeList.filter(
          (item) =>
            item.requesttitle === process.env.REACT_APP_EXCLUSIVE_PROMOTION
        );
        const deliveryCharges = chargeList.filter(
          (item) => item.requesttitle === process.env.REACT_APP_DELIVERY_CHARGES
        );
        const surchargeCharges = chargeList.filter(
          (item) =>
            item.requesttitle === process.env.REACT_APP_SURCHARGE_CHARGES
        );

        if (exclusivePromotions) {
          dispatch(
            updateRequiredExclusivePromotionCharges(exclusivePromotions)
          );
        }

        if (deliveryCharges) {
          dispatch(updateRequiredDeliveryCharges(deliveryCharges));
        }
        if (surchargeCharges) {
          dispatch(updateRequiredSurchargeCharges(surchargeCharges));
        }
      }

      dispatch(updateUserProfileDetails(userProfile));

      dispatch(updateSelectedTimeSlot(timeSlot));
      dispatch(updateBaseManualPricing(settings.enabledBaseManualPricing));
      dispatch(
        updateAgentPolicyManualPricing(settings.enabledAgentPolicyManualPricing)
      );

      if (settings.isExistingOrder) {
        // Check if we're in production environment
        const isProduction =
          process.env.NODE_ENV === "production" ||
          process.env.REACT_APP_ENV === "production";

        if (isProduction) {
          // PRODUCTION: Block the confirmation popup and direct to Order Management
          console.log(
            "Existing order detected - directing user to Order Management (PRODUCTION MODE)"
          );

          triggerAlert(
            "Current Path Unavailable",
            `Please go to Order Management to select and proceed with existing order [${settings.isExistingOrder.orderno}].`
          );

          // Hide MainButton progress and re-enable form
          webApp.MainButton.hideProgress(true);
          webApp.MainButton.enable();
          webApp.BackButton.show();
        } else if (FEATURE_FLAGS.ENABLE_ORDER_AMENDMENT_FLOW) {
          // DEVELOPMENT: Allow testing the amendment flow
          try {
            // console.log('Amendment flow enabled - showing confirmation dialog (DEVELOPMENT MODE)');

            // Store the amendment data for later use
            setPendingAmendmentData({
              existingOrder: settings.isExistingOrder,
              existingOrderItems: settings.existingOrderItems || [],
              userDetails: { name, phone, address, timeSlot },
              currentCartItems: cartItems,
            });

            // Show confirmation dialog
            setShowAmendmentDialog(true);

            // Hide MainButton progress while user decides
            webApp.MainButton.hideProgress(true);
          } catch (error) {
            console.error(
              "Amendment flow error, falling back to original behavior:",
              error
            );
            // FALLBACK: Original blocking behavior for safety
            triggerAlert(
              "Work In Progress",
              `Restart to amend order [${settings.isExistingOrder.orderno}].`
            );
          }
        } else {
          // FALLBACK: Original blocking behavior
          triggerAlert(
            "Work In Progress",
            `Restart to amend order [${settings.isExistingOrder.orderno}].`
          );
        }
      } else {
        navigate(PATH_PAYMENT, {
          replace: true,
        });
      }

      webApp.MainButton.hideProgress(true);
    } catch (error) {
      console.error("Error sending message:", error);
      webApp.MainButton.hideProgress(true);
      webApp.MainButton.enable();
      webApp.BackButton.show();
      // Handle the error gracefully, e.g., show an error message to the user
      alert(`${error}`);
    }
  };

  // Handler for confirming order amendment - navigate to ORDER MANAGEMENT page
  const handleAmendmentConfirm = async () => {
    if (!pendingAmendmentData) return;

    try {
      const { existingOrder, existingOrderItems, userDetails } =
        pendingAmendmentData;
      const { name, phone, address, timeSlot } = userDetails;

      console.log("=== NAVIGATING TO ORDER MANAGEMENT PAGE ===");
      console.log("Existing Order:", existingOrder);
      console.log("New Cart Items (to be added):", cartItems);
      console.log("Routing to Order Management instead of Amendment page");

      // Clean up dialog state
      setShowAmendmentDialog(false);
      setPendingAmendmentData(null);

      // Hide MainButton progress
      webApp.MainButton.hideProgress();

      // Route directly to ORDER MANAGEMENT screen
      navigate(PATH_ORDER_MANAGEMENT, {
        replace: true,
        state: {
          message: `Items will be added to existing order [${existingOrder.orderno}]. Please proceed from Order Management.`,
          fromNewToExisting: true,
          existingOrder: existingOrder,
          newItemsToAdd: cartItems,
        },
      });
    } catch (error) {
      console.error("Error navigating to order management page:", error);
      alert(`Error preparing order addition:\\n\\n${error.message}`);

      // Clean up state on error
      setShowAmendmentDialog(false);
      setPendingAmendmentData(null);
      webApp.MainButton.hideProgress();
      webApp.MainButton.enable();
      webApp.BackButton.show();
    }
  };

  // Handler for canceling order amendment
  const handleAmendmentCancel = () => {
    console.log("User canceled amendment - staying on User Details page");

    // Stay on User Details page - do not navigate anywhere
    // User needs to change their details to avoid existing order conflict

    // Clean up state
    setShowAmendmentDialog(false);
    setPendingAmendmentData(null);

    // Re-enable the form for user to modify details
    webApp.MainButton.hideProgress(true);
    webApp.MainButton.enable();
    webApp.BackButton.show();
  };

  // Handler for "Go to Order Management" button
  const handleGoToOrderManagement = () => {
    if (!pendingAmendmentData) return;

    try {
      const { existingOrder } = pendingAmendmentData;

      console.log("=== NAVIGATING TO ORDER MANAGEMENT (VIA BUTTON) ===");
      console.log("Existing Order:", existingOrder);

      // Clean up dialog state
      setShowAmendmentDialog(false);
      setPendingAmendmentData(null);

      // Hide MainButton progress
      webApp.MainButton.hideProgress();

      // Navigate directly to ORDER MANAGEMENT screen
      navigate(PATH_ORDER_MANAGEMENT, {
        replace: true,
        state: {
          message: `Please select existing order [${existingOrder.orderno}] to proceed.`,
          highlightOrderId: existingOrder.id,
          existingOrder: existingOrder,
        },
      });
    } catch (error) {
      console.error("Error navigating to order management:", error);
      alert(`Error opening Order Management:\\n\\n${error.message}`);

      // Clean up state on error
      setShowAmendmentDialog(false);
      setPendingAmendmentData(null);
      webApp.MainButton.hideProgress();
      webApp.MainButton.enable();
      webApp.BackButton.show();
    }
  };

  return (
    <TelegramScreen>
      <TelegramMiniMultiInputForm
        fieldlabel1={"Name"}
        fieldhint1={`John`}
        fieldlabel2={"Phone No."}
        fieldhint2={`8888 8888`}
        fieldlabel3={"Postal Code"}
        fieldhint3={`123456`}
        fieldlabel4={"Timeslot"}
        onSubmit={onMainClick}
      />

      {/* Order Amendment Confirmation Dialog - Only shown in development */}
      <OrderAmendmentConfirmationDialog
        isOpen={showAmendmentDialog}
        existingOrder={pendingAmendmentData?.existingOrder}
        existingOrderItems={pendingAmendmentData?.existingOrderItems}
        onConfirm={handleAmendmentConfirm}
        onCancel={handleAmendmentCancel}
        onGoToOrderManagement={handleGoToOrderManagement}
      />
    </TelegramScreen>
  );
};

export default ConfirmUserDetailsFormMessage;
